// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/verificationGate.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/verificationGate.js
//
// When a host has to verify their phone before publishing.
//
// ── What changed, and why ──────────────────────────────────────────────────
//
// It used to be asked on EVERY invitation, including someone's first — which
// is the worst possible moment. A person who has not yet seen what the product
// does is being asked for their phone number and an OTP before they get to
// find out. That is the single most expensive piece of friction in the whole
// flow, and it buys nothing at that point: their first invitation is almost
// always free and private, going to people who already know them.
//
// ── Why not simply drop it ─────────────────────────────────────────────────
//
// Because it is not decoration. It exists so a guest who gets an invitation
// from someone has some assurance there is a real, reachable person behind it,
// and so that abuse has a cost. Dropping it entirely gives that up everywhere,
// including the places it actually matters.
//
// ── Why there is no "from the second invitation" rule ──────────────────────
//
// It was the obvious idea and it does not survive the question "what is phone
// verification actually for here?".
//
// Sign-up is email-first and the email is verified with its own OTP, so by the
// time anyone reaches this gate their identity is already established. What a
// phone number adds is COST: disposable email addresses are free and endless,
// phone numbers are not. That cost is worth imposing where a stranger's
// invitation can do damage, and nowhere else.
//
// A count cannot tell those apart. It charges the frequent, legitimate host —
// the best kind of user — and is dodged by anyone who wants to dodge it with
// one throwaway invitation. So the test is what the invitation DOES:
//
//   * Asking guests for money (a contribution) — the host's UPI id goes on the
//     invitation and people send real money to it. This is the one case worth
//     asking on the very first invitation.
//   * Going out beyond the host's own circle (public, or listed in explore) —
//     reach is what makes spam worth sending.
//
// A free, unlisted invitation to twelve friends never asks — not the first,
// not the fiftieth. That is most invitations, and it is exactly the case where
// the friction was costing most and protecting least.
//
// ── When it is asked ───────────────────────────────────────────────────────
//
// At PUBLISH, not when the create screen opens. Two reasons, and the first is
// mechanical: privacy and pass type are what this rule reads, and neither
// exists until the host has chosen them. Asking on arrival could only ever
// guess.
//
// The second is that it is a far better moment. Someone who has filled in a
// title, a date, a venue and a cover is committed; the same question that
// would have made them close the tab on arrival is a small step at the end.
//
// Verification is once per account, so a host who trips this pays the cost a
// single time and never again.

/** Pass types where money changes hands between host and guest. */
const MONEY_PASS_TYPES = ['contribution', 'fixed', 'variable', 'paid'];

/**
 * Does this invitation need a verified phone before it can be published?
 *
 * @param {object} input
 * @param {boolean} input.phoneVerified   does the host already have one
 * @param {string}  input.passType        'free' | 'contribution' | …
 * @param {string}  input.privacy         'public' | 'private' | …
 * @param {boolean} input.showInExploreFeed
 * @returns {{required: boolean, reason: string|null}}
 *   `reason` is a key, not a sentence — the wording belongs to the screen that
 *   shows it, which knows whether it is a phone or a tablet and what the
 *   person was in the middle of doing.
 */
export function phoneVerificationGate({
  phoneVerified = false,
  passType = 'free',
  privacy = 'private',
  showInExploreFeed = false,
} = {}) {
  // Already done, and it is done once.
  if (phoneVerified) return { required: false, reason: null };

  if (MONEY_PASS_TYPES.includes(passType)) {
    return { required: true, reason: 'money' };
  }

  if (privacy === 'public' || showInExploreFeed) {
    return { required: true, reason: 'reach' };
  }

  // Deliberately nothing about how many they have made. See the note above:
  // a count charges good hosts and is trivially dodged by anyone else.
  return { required: false, reason: null };
}

/**
 * Why we are asking, in words a host would use.
 *
 * Here rather than in each screen so the app and the website give the same
 * answer — and because a prompt that does not say why reads as an obstacle
 * rather than a reason.
 */
export function verificationReasonText(reason) {
  switch (reason) {
    case 'money':
      return 'Your guests will be sending you money, so we verify your number first — it is what lets them trust the request.';
    case 'reach':
      return 'This invitation is public, so we verify your number before it goes out to people who may not know you.';
    default:
      return 'Verify your number to continue.';
  }
}

/**
 * The publish gate, for accounts that may have signed up with either a phone
 * or an email (or Google): the same rule as phoneVerificationGate for WHEN to
 * ask, and then whichever of the two the account does not have yet.
 *
 *   - An email / Google account is asked for its phone - the cost that keeps
 *     disposable accounts from sending money requests or public spam.
 *   - A phone account already carries that cost, and is asked for an email:
 *     a second, independent way to reach the host, and the one receipts and
 *     account recovery go to.
 *
 * A free, private invitation still never asks, for anyone.
 *
 * @param {object} input
 * @param {boolean} input.phoneVerified
 * @param {boolean} [input.emailVerified]  undefined = not known, treated as verified
 * @returns {{required: boolean, reason: string|null, need: 'phone'|'email'|null}}
 */
export function publishVerificationGate({
  phoneVerified = false,
  emailVerified,
  passType = 'free',
  privacy = 'private',
  showInExploreFeed = false,
} = {}) {
  // When: exactly the phone gate's rule, asked as if nothing were verified.
  const { required, reason } = phoneVerificationGate({ phoneVerified: false, passType, privacy, showInExploreFeed });
  if (!required) return { required: false, reason: null, need: null };

  if (!phoneVerified) return { required: true, reason, need: 'phone' };
  if (emailVerified === false) return { required: true, reason, need: 'email' };
  return { required: false, reason: null, need: null };
}

/** verificationReasonText, for an email rather than a phone. */
export function emailVerificationReasonText(reason) {
  switch (reason) {
    case 'money':
      return 'Your guests will be sending you money, so we need a verified email as well as your number - it is where receipts and anything about payments reach you.';
    case 'reach':
      return 'This invitation is public, so we need a verified email as well as your number before it goes out to people who may not know you.';
    default:
      return 'Verify your email to continue.';
  }
}
